import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectIssueTypes } from 'src/store/selectors/formOptions';
import { convertHTMLToString } from 'src/helpers/validationHelper';
import { onUrlImageExtensionExist } from 'src/helpers/CommonHelpers';
import { FaqFormData } from 'src/components/Faqs/CustomHooks/useFaqForm';

const HTML_TAG_PATTERN = /<[^>]+>/g;

const stripHtml = (html: string): string => html.replace(HTML_TAG_PATTERN, '');

type SaveAction = 'SaveAndClose' | 'SaveAndAdd';

interface UseFaqFormLogicOptions {
  formData: FaqFormData;
  setFormData: (data: Partial<FaqFormData>) => void;
  onSaveFaq: (faq: FaqFormData, action: SaveAction) => void;
}

export interface UseFaqFormLogicReturn {
  editorState: any;
  convertedTextAnswer: string;
  onEditorChange: (newEditorState: any) => void;
  onSelectDocument: (file: any) => void;
  handleSaveFaq: (actionType: SaveAction) => void;
  isFormValid: boolean;
}

export const useFaqFormLogic = ({
  formData,
  setFormData,
  onSaveFaq,
}: UseFaqFormLogicOptions): UseFaqFormLogicReturn => {
  const issueTypes: any[] = useSelector(selectIssueTypes);

  const initialAnswer: string = formData.answer || '';
  const initialPlainAnswer: string = initialAnswer
    ? stripHtml(initialAnswer)
    : '';

  const [editorState, setEditorState] = useState<any>(initialAnswer);
  const [convertedTextAnswer, setConvertedTextAnswer] =
    useState<string>(initialPlainAnswer);

  useEffect(() => {
    const issueTypeNames: string[] = issueTypes.map((it: any) => it.name);
    const nextTypeUpdate: Partial<FaqFormData> = { type: issueTypeNames };

    setFormData(nextTypeUpdate);
  }, [issueTypes, setFormData]);

  useEffect(() => {
    const nextEditorState: string = formData.answer || '';
    const nextPlainAnswer: string = formData.answer
      ? stripHtml(formData.answer)
      : '';

    setEditorState(nextEditorState);
    setConvertedTextAnswer(nextPlainAnswer);
  }, [formData.answer]);

  const onEditorChange = (newEditorState: any): void => {
    const plainAnswer: string = convertHTMLToString(newEditorState);
    setEditorState(newEditorState);
    setConvertedTextAnswer(plainAnswer);
  };

  const onSelectDocument = (file: any): void => {
    if (!file || file.length === 0) {
      return;
    }

    const [selectedFile] = file;
    const filePath: string = selectedFile?.path || selectedFile?.name || '';
    const isImage: boolean = onUrlImageExtensionExist(filePath);
    const nextUploadType: 'Image' | 'Doc' = isImage ? 'Image' : 'Doc';
    const nextAttachmentUpdate: Partial<FaqFormData> = {
      image: file,
      uploadType: nextUploadType,
    };

    setFormData(nextAttachmentUpdate);
  };

  const handleSaveFaq = (actionType: SaveAction): void => {
    const faqToSave: FaqFormData = { ...formData, answer: editorState };
    onSaveFaq(faqToSave, actionType);
  };

  const hasQuestion: boolean = !!formData.question?.trim();
  const hasAnswer: boolean = !!convertedTextAnswer?.trim();
  const isFormValid: boolean = hasQuestion && hasAnswer;

  return {
    editorState,
    convertedTextAnswer,
    onEditorChange,
    onSelectDocument,
    handleSaveFaq,
    isFormValid,
  };
};
